// --- CONFIGURATION & UTILITIES ---
// 1. **ACCURACY IMPROVEMENT:** Upgraded from Flash-preview to the latest stable Pro model for maximum reasoning and detail.
// NOTE: For production, this MUST be moved to a backend environment (like Node.js or Firebase Functions) for security.
// const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
// --- NEW: API Key Rotation ---
// NOTE: Make sure to update your .env file to use VITE_GEMINI_API_KEYS with comma-separated keys
const GEMINI_API_KEYS = (import.meta.env.VITE_GEMINI_API_KEYS || '').split(',').map(key => key.trim());
let currentApiKeyIndex = 0;

const getNextApiKey = () => {
  if (!GEMINI_API_KEYS.length || (GEMINI_API_KEYS.length === 1 && !GEMINI_API_KEYS[0])) {
    throw new Error("Gemini API keys are not configured. Please check your VITE_GEMINI_API_KEYS in the .env file.");
  }
  const key = GEMINI_API_KEYS[currentApiKeyIndex];
  currentApiKeyIndex = (currentApiKeyIndex + 1) % GEMINI_API_KEYS.length;
  return key;
};
// --- END NEW ---

const GEMINI_MODEL = 'gemini-2.5-flash'; // Use Pro for the highest quality analysis
// const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${GEMINI_API_KEY}`;

const getApiUrl = () => {
  const apiKey = getNextApiKey();
  // Ensure you are using the v1beta endpoint for the 3.1 preview models
  return `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${apiKey}`;
}



// Cleans the response text by removing markdown fences (```json ... ```)
const cleanJsonString = (text) => {
  // Regular expression to aggressively remove markdown fences and surrounding whitespace
  return text.replace(/^```json\s*/, '').replace(/\s*```$/, '');
};

// Converts a File object to a GoogleGenerativeAI.Part object (Base64 encoded data).
const fileToGenerativePart = async (file) => {
  const base64EncodedDataPromise = new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  });
  return {
    inlineData: { data: await base64EncodedDataPromise, mimeType: file.type },
  };
};

// --- CORE FUNCTIONS ---

/**
 * Analyzes a product image to extract its name, brand, category, and key ingredients.
 * @param {File} imageFile - The image file to analyze.
 * @returns {Promise<object>} A promise that resolves to the product details.
 */
export const analyzeProductImage = async (imageFile) => {
  // Prompt is already well-structured for clean data extraction
  const prompt = `
        Analyze the following image of a skincare product. Identify the product's brand, name, category, and key ingredients.
        Return the information in a valid JSON object with the following structure:
        {
          "brand": "The brand of the product",
          "name": "The full name of the product",
          "category": "The category of the product (e.g., Cleanser, Moisturizer, Serum, Sunscreen, etc.)",
          "keyIngredients": ["Ingredient 1", "Ingredient 2", "Ingredient 3"]
        }
    `;

  try {
    const imagePart = await fileToGenerativePart(imageFile);
    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }, imagePart] }],
        generationConfig: {
          "response_mime_type": "application/json",
        }
      })
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(`API request failed with status ${response.status}: ${errorBody.error.message}`);
    }

    const data = await response.json();
    const jsonString = cleanJsonString(data.candidates[0].content.parts[0].text);
    const result = JSON.parse(jsonString);

    return {
      brand: result.brand || '',
      name: result.name || '',
      category: result.category || '',
      ingredients: result.keyIngredients?.join(', ') || '',
    };

  } catch (error) {
    console.error("Error analyzing product image:", error);
    throw new Error(`Failed to analyze product image. Details: ${error.message}`);
  }
};

/**
 * A dedicated function to check the compatibility between two skincare ingredients.
 * @param {string} ing1Name - The name of the first ingredient.
 * @param {string} ing2Name - The name of the second ingredient.
 * @returns {Promise<object>} A promise that resolves to the compatibility analysis object.
 */
export const fetchIngredientCompatibility = async (ing1Name, ing2Name) => {
  // 2. **ACCURACY IMPROVEMENT:** Refined prompt for scientific rigor and standardized terms.
  const prompt = `
        Act as a **clinical formulation scientist and board-certified dermatologist**. Analyze the compatibility between **${ing1Name}** and **${ing2Name}**. Base your analysis on **chemical properties (e.g., pH, oxidation risk)** and **documented clinical interactions**.
        Provide a concise, valid JSON object with the following structure. Do not include any markdown formatting or other text outside of the JSON object.
        {
          "compatibility": "excellent" | "good" | "caution" | "avoid",
          "message": "A one-sentence explanation of the compatibility level, citing the chemical or clinical reason.",
          "recommendations": ["Up to 3 key recommendations for usage as strings (e.g., 'Use in separate routines,' 'Buffer with moisturizer')."]
        }
    `;

  try {
    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          "response_mime_type": "application/json",
        }
      })
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(`API request failed with status ${response.status}: ${errorBody.error.message}`);
    }

    const data = await response.json();
    const jsonString = cleanJsonString(data.candidates[0].content.parts[0].text);
    const result = JSON.parse(jsonString);

    // --- Data Normalization ---
    return {
      compatibility: result.compatibility || 'unknown',
      message: result.message || 'No analysis message was provided.',
      recommendations: Array.isArray(result.recommendations) ? result.recommendations : []
    };

  } catch (error) {
    console.error("Error during ingredient compatibility check:", error);
    throw new Error(`Failed to get compatibility analysis from AI. Details: ${error.message}`);
  }
};

/**
 * Analyzes an existing skincare routine based on products and ingredients.
 */
export const fetchGeminiAnalysis = async (routine) => {

  // Prompt is largely kept the same as it's well-structured, but adding the clinical persona
  const prompt = `
        Act as an **expert cosmetic chemist and board-certified dermatologist**. Analyze the following skincare routine and return a single, valid JSON object.

        **CRITICAL INSTRUCTION: You MUST provide a 'score' from 0-10 for EVERY single product listed in the morning, evening, and weekly routines. This field is MANDATORY and cannot be omitted under any circumstances.**

        Do not include any text or markdown formatting before or after the JSON object.

        **User's Routine (Analyze for conflicts, concentration, and goals):**
        - **Morning:** ${JSON.stringify(routine.morningProducts.map(p => ({ id: p.id, name: p.name, category: p.category, ingredients: p.ingredients })))}
        - **Evening:** ${JSON.stringify(routine.eveningProducts.map(p => ({ id: p.id, name: p.name, category: p.category, ingredients: p.ingredients })))}
        - **Weekly:** ${JSON.stringify(routine.weeklyTreatments.map(p => ({ id: p.id, name: p.name, category: p.category, ingredients: p.ingredients })))}

        // ... (JSON Output Structure kept for brevity, as it was already robust) ...
        
        {
          "overallScore": {
            "score": "number (0-100)",
            "rating": "string (e.g., 'Excellent', 'Needs Improvement')",
            "improvement": "string (A one-sentence summary of the key area for improvement)",
            "insights": [
              {"text": "string (A key insight about the overall routine, such as a missing product category or a major conflict)", "icon": "string (Icon name from lucide-react, e.g., 'ShieldCheck', 'FlaskConical')", "type": "string (e.g., 'suggestion', 'warning')"}
            ]
          },
          "metrics": {
            "Effectiveness": {
              "score": "number (0-100)",
              "rating": "string (e.g., 'Excellent', 'Good', 'Fair', 'Poor')",
              "explanation": "string (Detailed explanation of why this score was given, considering product types, active ingredients, and routine structure.)"
            },
            "Safety": {
              "score": "number (0-100)",
              "rating": "string (e.g., 'Excellent', 'Good', 'Fair', 'Poor')",
              "explanation": "string (Detailed explanation of the safety score, focusing on potential irritants, ingredient concentrations, and compatibility issues.)"
            },
            "Goal Alignment": {
              "score": "number (0-100)",
              "rating": "string (e.g., 'Excellent', 'Good', 'Fair', 'Poor')",
              "explanation": "string (Detailed explanation of how well the routine aligns with common goals like anti-aging, hydration, or acne control.)"
            },
            "Routine Consistency": {
              "score": "number (0-100)",
              "rating": "string (e.g., 'Excellent', 'Good', 'Fair', 'Poor')",
              "explanation": "string (Detailed explanation of how consistent the routine is, e.g., using key products daily.)"
            }
          },
          "morningRoutine": {
            "score": "number (0-100)",
            "analysis": "string (A detailed paragraph analyzing the morning routine's strengths and weaknesses)",
            "products": [
                {
                  "id": "string (The original product ID)",
                  "score": "number (0-10, REQUIRED score for the individual product)",
                  "rating": "string (e.g., 'Excellent', 'Good', 'Fair', 'Poor')",
                  "issues": ["string (List any specific issues or conflicts with this product in the routine)"]
                }
            ],
            "insights": [
              {"text": "string (A specific, actionable insight for the morning routine. Include suggestions for missing products (e.g., 'Consider adding an antioxidant serum'), good pairings (e.g., 'Vitamin C and sunscreen work well together'), or potential issues.)", "icon": "string (e.g., 'Sun', 'PlusCircle')", "type": "string (e.g., 'pro', 'suggestion', 'warning')"}
            ]
          },
          "eveningRoutine": {
            "score": "number (0-100)",
            "analysis": "string (A detailed paragraph analyzing the evening routine's strengths and weaknesses)",
            "products": [
                {
                  "id": "string (The original product ID)",
                  "score": "number (0-10, REQUIRED score for the individual product)",
                  "rating": "string (e.g., 'Excellent', 'Good', 'Fair', 'Poor')",
                  "issues": ["string (List any specific issues or conflicts with this product in the routine)"]
                }
            ],
            "insights": [
              {"text": "string (A specific, actionable insight for the evening routine. Include suggestions like using retinoids, avoiding conflicts, or adding a specific type of moisturizer.)", "icon": "string (e.g., 'Moon', 'Zap')", "type": "string (e.g., 'pro', 'suggestion', 'warning')"}
            ]
          },
          "weeklyRoutine": {
            "score": "number (0-100)",
            "analysis": "string (A detailed paragraph analyzing the weekly routine's strengths and weaknesses)",
            "products": [
                {
                  "id": "string (The original product ID)",
                  "score": "number (0-10, REQUIRED score for the individual product)",
                  "rating": "string (e.g., 'Excellent', 'Good', 'Fair', 'Poor')",
                  "issues": ["string (List any specific issues or conflicts with this product in the routine)"]
                }
            ],
            "insights": [
              {"text": "string (A specific, actionable insight for the weekly routine. Include suggestions like using exfoliants or masks.)", "icon": "string (e.g., 'Sparkles', 'Calendar')", "type": "string (e.g., 'pro', 'suggestion', 'warning')"}
            ]
          },
          "detailedIngredientAnalysis": [
            {
              "name": "string (e.g., 'Retinol')",
              "purpose": "string (e.g., 'Anti-aging, cell turnover')",
              "strength": "string (e.g., 'High', 'Moderate', 'Low')",
              "products": ["string (List of product names containing this ingredient)"],
              "suggestions": ["string (Suggestions for using this ingredient, e.g., 'Start with a lower concentration')"],
              "warnings": ["string (Potential side effects or interactions, e.g., 'Can cause dryness and irritation')"],
              "evidence": {
                "summary": "string (A brief summary of the scientific evidence for this ingredient's effectiveness)",
                "url": "string (A link to a reputable source, like an NCBI study or dermatology journal)"
              }
            }
          ],
          "ingredientCompatibility": [
            {
              "ingredientA": "string (e.g., 'Vitamin C')",
              "ingredientB": "string (e.g., 'Niacinamide')",
              "compatible": "boolean",
              "reason": "string (A detailed explanation of why the ingredients are or are not compatible, and how to use them safely if they are not)"
            }
          ]
        }
    `;

  try {
    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          "response_mime_type": "application/json",
        }
      })
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(`API request failed with status ${response.status}: ${errorBody.error.message}`);
    }

    const data = await response.json();
    // console.log('RAW Gemini response:', JSON.stringify(data, null, 2));
    const jsonString = cleanJsonString(data.candidates[0].content.parts[0].text);
    const analysisResult = JSON.parse(jsonString);
    // console.log('PARSED analysis result:', analysisResult);
    // console.log('Morning products from AI:', analysisResult.morningRoutine.products);


    // --- Data Normalization and Merge (Your existing logic, which is good) --- 
    // ... (Normalization logic omitted for brevity, keeping original for full code) ...
    analysisResult.overallScore = analysisResult.overallScore || { score: 0, rating: 'N/A', improvement: '', insights: [] };
    analysisResult.overallScore.insights = analysisResult.overallScore.insights || [];
    analysisResult.metrics = analysisResult.metrics || {};
    analysisResult.morningRoutine = analysisResult.morningRoutine || { score: 0, analysis: '', products: [], insights: [] };
    analysisResult.morningRoutine.products = analysisResult.morningRoutine.products || [];
    analysisResult.morningRoutine.insights = analysisResult.morningRoutine.insights || [];
    analysisResult.eveningRoutine = analysisResult.eveningRoutine || { score: 0, analysis: '', products: [], insights: [] };
    analysisResult.eveningRoutine.products = analysisResult.eveningRoutine.products || [];
    analysisResult.eveningRoutine.insights = analysisResult.eveningRoutine.insights || [];
    analysisResult.weeklyRoutine = analysisResult.weeklyRoutine || { score: 0, analysis: '', products: [], insights: [] };
    analysisResult.weeklyRoutine.products = analysisResult.weeklyRoutine.products || [];
    analysisResult.weeklyRoutine.insights = analysisResult.weeklyRoutine.insights || [];
    analysisResult.detailedIngredientAnalysis = analysisResult.detailedIngredientAnalysis || [];
    analysisResult.ingredientCompatibility = analysisResult.ingredientCompatibility || [];
    analysisResult.productRecommendations = analysisResult.productRecommendations || [];

    const mergeProductData = (originalProducts, analyzedProducts) => {
      if (!analyzedProducts) return originalProducts;

      return originalProducts.map(origP => {
        const analyzedP = analyzedProducts.find(p => {
          if (p.id && origP.id) {
            return p.id.toString() === origP.id.toString();
          }
          if (p.name && origP.name) {
            return p.name.toLowerCase().trim() === origP.name.toLowerCase().trim();
          }
          return false;
        });
        return { ...origP, ...analyzedP };
      });
    };


    analysisResult.morningRoutine.products = mergeProductData(routine.morningProducts, analysisResult.morningRoutine.products);
    analysisResult.eveningRoutine.products = mergeProductData(routine.eveningProducts, analysisResult.eveningRoutine.products);
    analysisResult.weeklyRoutine.products = mergeProductData(routine.weeklyTreatments, analysisResult.weeklyRoutine.products);

    return analysisResult;

  } catch (error) {
    console.error("Error fetching Gemini analysis:", error);
    if (error.message.includes('API key not valid')) {
      throw new Error("Invalid Gemini API Key. Please check the VITE_GEMINI_API_KEY in your .env file.");
    }
    throw new Error("Could not get a new analysis from the AI. Please check your API key and network connection.");
  }
};




/**
 * Creates a recommended routine based on a user assessment (no existing products).
 */
export const fetchGeminiAnalysisFromAssessment = async (answers, questions) => {

  const formattedAnswers = questions.map((q, index) => {
    const answer = answers[index];
    if (!answer) return null;

    let answerDisplay;
    switch (q.type) {
      case 'single-select':
      case 'image-select':
        answerDisplay = q.options.find(opt => opt.value === answer)?.label || answer;
        break;
      case 'multi-select':
        answerDisplay = answer.map(val => q.options.find(opt => opt.value === val)?.label || val).join(', ');
        break;
      case 'slider':
        answerDisplay = `${answer} out of ${q.max}`;
        break;
      default:
        answerDisplay = answer;
    }
    return {
      question: q.title,
      answer: answerDisplay
    };
  }).filter(Boolean);


  const prompt = `
        Act as an **expert dermatologist and skincare consultant**. Based on the user's answers to the following skin assessment, generate a complete and personalized skincare scorecard and a FOUNDATIONAL RECOMMENDED ROUTINE. The user has NOT provided an existing routine; you are to CREATE a recommended routine for them from scratch.

        **User's Assessment Answers (Base your routine on these needs and goals):**
        ${JSON.stringify(formattedAnswers, null, 2)}

        **Your Task:**
        Generate a valid JSON object that provides a comprehensive skincare analysis and a recommended daily and weekly routine. The JSON object must strictly follow the structure below. For each product, provide detailed usage instructions. **Ensure the recommended products use generic but highly descriptive names (e.g., 'Broad-Spectrum Mineral Sunscreen SPF 30')**.

        // ... (JSON Output Structure kept for brevity, as it was already robust) ...

        {
            "overallScore": {
              "score": "number (0-100, This is a projected score if the user follows your recommended routine)",
              "rating": "string (e.g., 'Excellent Potential', 'Strong Foundation')",
              "improvement": "string (A one-sentence summary of how the new routine will help their primary concerns)",
              "insights": [
                {"text": "string (A key insight about the user's skin based on their answers)", "icon": "string (e.g., 'ShieldCheck', 'FlaskConical')", "type": "string ('suggestion')"}
              ]
            },
            "metrics": {
              "Effectiveness": { "score": 95, "rating": "Excellent", "explanation": "This routine is highly effective because it directly targets the user's stated concerns (e.g., acne, aging) with proven active ingredients in a consistent structure." },
              "Safety": { "score": 90, "rating": "Excellent", "explanation": "The routine is designed to be safe by starting with lower concentrations of active ingredients and avoiding common irritants, based on the user's sensitivity level." },
              "Goal Alignment": { "score": 98, "rating": "Excellent", "explanation": "Every product in this recommended routine is chosen specifically to address the user's primary goals, such as reducing hyperpigmentation and improving skin texture." },
              "Routine Consistency": { "score": 100, "rating": "Excellent", "explanation": "This foundational routine is designed for easy and consistent daily use, which is critical for achieving long-term results." }
            },
            "morningRoutine": {
              "score": "number (90-100, a high score reflecting a well-designed routine)",
              "analysis": "string (A detailed paragraph explaining WHY this morning routine is ideal for the user. Mention the purpose of each product type, e.g., 'The gentle cleanser removes overnight buildup without stripping the skin, the antioxidant serum protects against environmental damage...')",
              "products": [
                  {
                    "id": "m-rec-1",
                    "name": "string (Generic but descriptive product name, e.g., 'Hydrating Cream Cleanser')",
                    "category": "Cleanser",
                    "ingredients": ["string (List 2-3 key beneficial ingredients, e.g., 'Ceramides', 'Glycerin')"],
                    "usage": "string (e.g., 'Daily, in the morning')",
                    "quantity": "string (e.g., 'A dime-sized amount')",
                    "application": "string (e.g., 'Gently massage onto damp skin for 60 seconds, then rinse with lukewarm water.')",
                    "tips": "string (e.g., 'Avoid using hot water, which can strip the skin's natural oils.')",
                    "score": 10, "rating": "Excellent", "issues": []
                  }
              ]
            },
            "eveningRoutine": {
              "score": "number (90-100, a high score reflecting a well-designed routine)",
              "analysis": "string (A detailed paragraph explaining the evening routine's focus on repair and treatment, e.g., 'The evening is for cleansing the day away and using targeted treatments... The double cleanse ensures all impurities are removed...')",
              "products": [
                   {
                    "id": "e-rec-1",
                    "name": "string (e.g., 'Micellar Water or Cleansing Balm')",
                    "category": "Cleanser",
                    "ingredients": ["string (e.g., 'Glycerin', 'Micelles')"],
                    "usage": "string (e.g., 'Daily, in the evening')",
                    "quantity": "string (e.g., 'A cotton pad soaked, or a pea-sized amount')",
                    "application": "string (e.g., 'Apply to dry skin to break down makeup and sunscreen before your main cleanser.')",
                    "tips": "string (e.g., 'This is the first step of a double cleanse, crucial for removing oil-based impurities.')",
                    "score": 10, "rating": "Excellent", "issues": []
                  }
              ]
            },
            "weeklyRoutine": {
                "analysis": "string (A paragraph explaining the importance of weekly treatments for the user's goals, like exfoliation for texture or a hydrating mask for dryness.)",
                "products": [
                    {
                        "id": "w-rec-1",
                        "name": "string (e.g., 'AHA/BHA Exfoliating Serum')",
                        "category": "Exfoliant",
                        "usage": "string (e.g., '2-3 times per week, at night')",
                        "quantity": "string (e.g., 'A few drops')",
                        "application": "string (e.g., 'Apply to clean, dry skin. Leave on for 10 minutes, then rinse.')",
                        "tips": "string (e.g., 'Avoid using on the same night as other strong actives like retinoids.')",
                        "ingredients": ["string (e.g., 'Glycolic Acid', 'Salicylic Acid')"],
                        "score": 10,
                        "rating": "Excellent",
                        "issues": []
                    }
                ]
            },
            "detailedIngredientAnalysis": [],
            "ingredientCompatibility": [],
            "productRecommendations": []
          }
    `;

  try {
    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          "response_mime_type": "application/json",
        }
      })
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(`API request failed with status ${response.status}: ${errorBody.error.message}`);
    }

    const data = await response.json();
    const jsonString = cleanJsonString(data.candidates[0].content.parts[0].text);
    const analysisResult = JSON.parse(jsonString);

    // ... (Data Normalization logic omitted for brevity, keeping original for full code) ...
    analysisResult.overallScore = analysisResult.overallScore || { score: 0, rating: 'N/A', improvement: '', insights: [] };
    analysisResult.metrics = analysisResult.metrics || {};
    analysisResult.morningRoutine = analysisResult.morningRoutine || { score: 0, analysis: '', products: [], insights: [] };
    analysisResult.eveningRoutine = analysisResult.eveningRoutine || { score: 0, analysis: '', products: [], insights: [] };
    analysisResult.weeklyRoutine = analysisResult.weeklyRoutine || { analysis: '', products: [], insights: [] };
    analysisResult.detailedIngredientAnalysis = analysisResult.detailedIngredientAnalysis || [];
    analysisResult.ingredientCompatibility = analysisResult.ingredientCompatibility || [];
    analysisResult.productRecommendations = analysisResult.productRecommendations || [];

    analysisResult.routine = {
      morningProducts: analysisResult.morningRoutine.products,
      eveningProducts: analysisResult.eveningRoutine.products,
      weeklyTreatments: analysisResult.weeklyRoutine.products,
    };

    return analysisResult;

  } catch (error) {
    console.error("Error fetching Gemini analysis from assessment:", error);
    if (error.message.includes('API key not valid')) {
      throw new Error("Invalid Gemini API Key. Please check the VITE_GEMINI_API_KEY in your .env file.");
    }
    throw new Error("Could not get a new analysis from the AI. Please check your API key and network connection.");
  }
};

/**
 * Generates actionable optimization suggestions based on a completed analysis.
 */
export const getOptimizedRoutine = async (analysis) => {
  // Prompt is already very strong and detailed.
  const prompt = `
        You are an expert dermatologist and AI skincare consultant. Your task is to provide actionable optimization suggestions based on a user's skincare scorecard analysis. The analysis provided is a JSON object that includes overall scores, metric breakdowns, and detailed analysis of morning and evening routines.

        **User's Skincare Analysis:**
        ${JSON.stringify(analysis, null, 2)}

        **Your Task:**
        Generate a valid JSON object containing a list of 3 to 5 personalized, **high-impact, and actionable suggestions** to improve the user's routine. Focus on the most impactful changes first. The suggestions should be easy to understand, scientifically grounded, and implementable for a regular user.

        // ... (JSON Output Structure kept for brevity, as it was already robust) ...
        {
          "suggestions": [
            {
              "icon": "string (A relevant icon name from lucide-react, e.g., 'PlusCircle', 'Repeat', 'Sunrise', 'Layers', 'Droplets')",
              "title": "string (A short, catchy title for the suggestion, e.g., 'Introduce Double Cleansing')",
              "category": "string (e.g., 'Product Addition', 'Ingredient Swap', 'Routine Timing', 'Application Technique')",
              "description": "string (A brief, one-sentence summary of the suggestion and its main benefit. E.g., 'Add a Vitamin C serum in the morning to boost sun protection and fight free radicals.')",
              "details": "string (A detailed paragraph explaining the rationale behind the suggestion. Explain what problem it solves based on their analysis (e.g., 'Your routine is missing a dedicated antioxidant...'), how to implement it (e.g., 'Apply 2-3 drops after cleansing and before moisturizing...'), and what to expect. Be specific and scientific where appropriate.)"
            }
          ]
        }
    `;

  try {
    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          "response_mime_type": "application/json",
        }
      })
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(`API request failed with status ${response.status}: ${errorBody.error.message}`);
    }

    const data = await response.json();
    const jsonString = cleanJsonString(data.candidates[0].content.parts[0].text);
    const optimizationResult = JSON.parse(jsonString);

    if (!optimizationResult.suggestions) {
      optimizationResult.suggestions = [];
    }

    return optimizationResult;

  } catch (error) {
    console.error("Error fetching Gemini optimization:", error);
    if (error.message.includes('API key not valid')) {
      throw new Error("Invalid Gemini API Key. Please check the VITE_GEMINI_API_KEY in your .env file.");
    }
    throw new Error("Could not get optimization suggestions from the AI. Please check your API key and network connection.");
  }
};


/**
 * Performs the core, visual skin analysis on a user-uploaded image.
 * This is the most critical function for accuracy.
 */
export const analyzeDermaScanImage = async (imageFile) => {
  // 2. **ACCURACY IMPROVEMENT:** Refined prompt for clinical focus and mandatory object localization.
  const prompt = `
        As an expert dermatologist and **Gemini Vision Specialist**, analyze the user's face in the provided image. Generate a detailed, scientifically-grounded skin analysis. Your response must be a single, valid JSON object, without any markdown formatting or surrounding text.

        **Core Mandates for High Accuracy:**
        1. **Clinical Focus:** Analyze subtle visual cues like **texture granularity**, **micro-inflammation**, and **oil reflection patterns** to support your conclusions.
        2. **Be Precise:** Scores should be calculated based on visual evidence.
        3. **Object Localization:** For the 'concernAreas' array, you MUST use your internal vision model to determine the coordinates of the detected concern and report them as **X/Y percentages (0-100)** relative to the face area.

        **JSON Output Structure:**
        {
          "keyInsights": [
            {
              "icon": "string (lucide-react icon name, e.g., 'Droplets', 'Shield', 'Zap', 'Wind', 'Sun')",
              "text": "string (A concise, high-impact insight, e.g., 'Hydration is a key area for improvement.')",
              "type": "'warning' | 'suggestion' | 'pro'"
            }
          ],
          "skinHealth": "number (0-100, an overall score based on all metrics)",
          "skinAge": "number (estimated age of the skin based on visual evidence)",
          "analysis": {
            "Hydration & Texture": {
              "score": "number (0-100)",
              "analysis": "string (Detailed, multi-sentence analysis of skin texture, pore size, and hydration levels.)",
              "color": "blue-500"
            },
            "skinType": {
              "type": "'Oily', 'Dry', 'Combination', 'Normal', 'Sensitive'",
              "confidence": "number (0-100)",
              "analysis": "string (A detailed, multi-sentence explanation for the determined skin type, referencing visual cues like pore size, shine, and texture.)"
            },
            "Oiliness & Pores": {
              "score": "number (0-100)",
              "analysis": "string (Detailed, multi-sentence analysis of sebum production, pore congestion, and T-zone activity.)",
              "color": "green-500"
            },
            "Redness & Blemishes": {
              "score": "number (0-100)",
              "analysis": "string (Detailed, multi-sentence analysis of inflammation, active blemishes, and post-inflammatory erythema.)",
              "color": "red-500"
            },
            "Wrinkles & Fine Lines": {
              "score": "number (0-100)",
              "analysis": "string (Detailed, multi-sentence analysis of dynamic and static wrinkles, focusing on areas like the forehead, eyes, and mouth.)",
              "color": "purple-500"
            },
            "Pigmentation & Tone": {
              "score": "number (0-100)",
              "analysis": "string (Detailed, multi-sentence analysis of sun spots, melasma, and overall skin tone evenness.)",
              "color": "yellow-500"
            }
          },
          "recommendations": [
            { "title": "string (Specific product or lifestyle advice)", "description": "string (Detailed explanation of the recommendation and its benefits.)" }
          ],
          "concernAreas": [
            { "x": "number (0-100, x-percentage coordinate)", "y": "number (0-100, y-percentage coordinate)", "concern": "string (A specific, descriptive label for the concern, e.g., 'Forehead Wrinkle', 'Cheek Redness')" }
          ]
        }
    `;

  try {
    const imagePart = await fileToGenerativePart(imageFile);
    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }, imagePart] }],
        generationConfig: {
          "response_mime_type": "application/json",
        }
      })
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(`API request failed with status ${response.status}: ${errorBody.error.message}`);
    }

    const data = await response.json();
    const jsonString = cleanJsonString(data.candidates[0].content.parts[0].text);
    const result = JSON.parse(jsonString);

    // --- Data Normalization ---
    return {
      ...result,
      keyInsights: result.keyInsights || [],
      analysis: result.analysis || {},
      recommendations: result.recommendations || [],
      concernAreas: result.concernAreas || [],
    };

  } catch (error) {
    console.error("Error analyzing derma scan image:", error);
    throw new Error(`Failed to analyze derma scan image. Details: ${error.message}`);
  }
};